import { cx } from "../../../utils";
import { LabelSize, type LabelProps } from "./types";

export { LabelSize } from "./types";
export type { LabelProps } from "./types";

// Bottom spacing is part of the size step: SM labels sit tighter to their
// input, BASE labels head a group of fields. Override via `className`.
const sizeClass = {
  [LabelSize.SM]: "text-sm mb-1",
  [LabelSize.BASE]: "text-base mb-2"
};

const OPTIONAL_TEXT: Record<string, string> = {
  en: "(optional)",
  es: "(opcional)"
};

export const getOptionalText = (locale = "en") =>
  OPTIONAL_TEXT[locale] || OPTIONAL_TEXT.en;

/**
 * Shared form label used by every input in `shared/components/forms`.
 *
 * Owns the three pieces of label chrome so they stay identical everywhere:
 * the label text, the required mark (`*`) and the optional suffix
 * (`(optional)` / `(opcional)`).
 *
 * `showRequired` and `showOptional` are independent booleans on purpose — each
 * input derives them from its own state (`required`, `readOnly`, `disabled`,
 * `hideOptional`) and passes the resolved answer down.
 *
 * @param as - element to render: `label` (default), `legend` for fieldsets, or `span` when the label already sits inside a `<label>`
 * @param showRequired - renders a trailing `*` after the label text
 * @param showOptional - renders a trailing `(optional)` suffix
 * @param optionalText - overrides the optional suffix copy (wins over `locale`)
 */
export const Label = ({
  children,
  as: element = "label",
  htmlFor,
  id,
  className,
  size = LabelSize.SM,
  error,
  showRequired,
  showOptional,
  optionalText,
  locale
}: LabelProps) => {
  // `htmlFor` only exists on <label>, so TSX rejects it against the
  // "label" | "legend" | "span" union. Narrow once, guard the prop below.
  const Tag = element as "label";

  return (
    <Tag
      id={id}
      htmlFor={element === "label" ? htmlFor : undefined}
      className={cx(
        "block font-medium",
        sizeClass[size],
        error ? "text-danger-500" : "text-content-700",
        className
      )}
    >
      {children}
      {showRequired ? <span className="text-content-700"> *</span> : null}
      {showOptional ? (
        <span className="text-content-100">
          {" "}
          {optionalText || getOptionalText(locale)}
        </span>
      ) : null}
    </Tag>
  );
};
